import type { MessageStatus } from '../../@Enums/PHP/ServicesV2/Messages/DataProviders/MessageStatus'

// The API #548 approval contract (`/v2/Messages/Approvals` and the
// `queue_service_message_for_approval` result), with #549's reply target. Every object is closed
// and every listed key is required.

export type MessageApprovalServiceType = 'quote' | 'invoice'

export interface MessageApprovalTemplate {
  templateGUID: string
  templateName: string
}

export interface MessageApprovalRecipient {
  addressType: 'to' | 'cc' | 'bcc'
  email: string
  name: string | null
}

export interface MessageApprovalAttachment {
  source: 'generatedServicePdf' | 'filingCabinet'
  fileName: string
  mimeType: string
  byteSize: number
}

export interface MessageApprovalDecisionRecord {
  outcome: 'approved' | 'rejected'
  byUserGUID: string
  at: string
}

/** The external message an approved email replies to (#549); null for a new email. */
export interface MessageApprovalReplyTarget {
  mailboxGUID: string
  mailboxAddress: string
  messageID: string
  mode: 'reply' | 'replyAll'
  original: { subject: string; from: string; receivedAt: string }
}

export interface MessageApprovalView {
  messageGUID: string
  status: MessageStatus
  statusLabel: string
  revision: string
  serviceType: MessageApprovalServiceType
  serviceGUIDs: string[]
  template: MessageApprovalTemplate
  emailAccountGUID: string | null
  subject: string
  bodyText: string
  bodyHtmlBytes: number
  recipients: MessageApprovalRecipient[]
  attachments: MessageApprovalAttachment[]
  requestedByUserGUID: string | null
  requestedAt: string
  decision: MessageApprovalDecisionRecord | null
  replyTo: MessageApprovalReplyTarget | null
}

/** The single-item HTTP view: the approval view plus the rendered HTML body. */
export type MessageApprovalDetail = MessageApprovalView & { bodyHtml: string }

export interface MessageApprovalPage {
  approvals: MessageApprovalView[]
  page: number
  limit: number
  nextPage: number | null
}

export interface MessageApprovalDecision {
  approval: MessageApprovalView
  replayed: boolean
}

/** A `queue_service_message_for_approval` result; it never carries `bodyHtml`. */
export interface MessageApprovalRequestResult {
  kind: 'message_approval_request'
  replayed: boolean
  approval: MessageApprovalView
}

/* eslint-disable no-unused-vars -- Core ESLint flags TypeScript enum contract members. */
export enum MessageApprovalOperation {
  List = 'list',
  Get = 'get',
  Approve = 'approve',
  Reject = 'reject',
}

export enum MessageApprovalErrorCode {
  InvalidArgument = 'INVALID_ARGUMENT',
  AuthenticationRequired = 'AUTHENTICATION_REQUIRED',
  Forbidden = 'FORBIDDEN',
  NotFound = 'NOT_FOUND',
  NotPending = 'MESSAGE_APPROVAL_NOT_PENDING',
  ContentChanged = 'MESSAGE_CONTENT_CHANGED',
  PayloadTooLarge = 'PAYLOAD_TOO_LARGE',
  StorageUnavailable = 'STORAGE_UNAVAILABLE',
  /** Client-side: a 2xx response that failed validation, or an argument the client refused. */
  InvalidResponse = 'INVALID_RESPONSE',
  /** Client-side: any other failure (network, timeout, an untyped error). */
  RequestFailed = 'REQUEST_FAILED',
}
/* eslint-enable no-unused-vars */

export type MessageApprovalOutcome = 'not_applied' | 'unknown'

export interface MessageApprovalFailure {
  code: MessageApprovalErrorCode
  message: string
  retryAfterSeconds: number | null
  outcome: MessageApprovalOutcome
}

export type MessageApprovalEntryStatus = 'loading' | 'ready' | 'deciding' | 'failed'

export type MessageApprovalAction = 'approve' | 'reject'

/**
 * One approval as the store holds it. `failure` explains the last refused or uncertain request;
 * after a conflict or an uncertain decision the entry is `ready` again with the re-read detail and
 * keeps that failure. `unknownAction` names a decision whose outcome was not confirmed.
 */
export interface MessageApprovalEntry {
  status: MessageApprovalEntryStatus
  detail: MessageApprovalDetail | null
  failure: MessageApprovalFailure | null
  unknownAction?: MessageApprovalAction
}

/**
 * The frontend ability subjects of the four approval routes (`endpoint:uriSegments`, a GUID
 * segment as `Guid`). Hand-declared, as `reconciliationSubjects` is: the generated subject enum
 * changes only through the API's enum generator.
 */
export const messageApprovalSubjects = {
  list: 'Messages:Approvals',
  view: 'Messages:Approvals:Guid',
  approve: 'Messages:Approvals:Guid:Approve',
  reject: 'Messages:Approvals:Guid:Reject',
} as const

export const MESSAGE_APPROVAL_LIMIT_MAX = 50
export const MESSAGE_APPROVAL_RECIPIENTS_MAX = 500
export const MESSAGE_APPROVAL_ATTACHMENTS_MAX = 100
export const MESSAGE_APPROVAL_SERVICES_MAX = 26
